"use client";

import { Menu, X } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { usePathname } from "next/navigation";
import {
  startTransition,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";

import { SITE_CONTENT_OUTER, SITE_NAV_EXPANDED_ROW } from "@/src/landing/site-content-shell";
import { trackEvent } from "@/src/lib/gtm-track";

/** Secciones con `id` en la landing (scroll spy). `ejemplos` solo por ruta, no hay elemento en el DOM de la home. */
type LandingNavSectionId =
  | "inicio"
  | "como-funciona"
  | "ejemplos"
  | "precios"
  | "preguntas-frecuentes"
  | "soporte";

const HOME_SCROLL_SECTION_ORDER: readonly LandingNavSectionId[] = [
  "inicio",
  "como-funciona",
  "precios",
  "preguntas-frecuentes",
  "soporte",
];

/** Línea de detección: más baja en viewport (~navbar + margen) para que el indicador cambie ~100px antes al bajar. */
const SCROLL_MARKER_PX = 204;

function computeActiveHomeSection(): LandingNavSectionId {
  let active: LandingNavSectionId = "inicio";
  for (const id of HOME_SCROLL_SECTION_ORDER) {
    const el = document.getElementById(id);
    if (!el) continue;
    if (el.getBoundingClientRect().top <= SCROLL_MARKER_PX) active = id;
  }
  return active;
}

/** Tipografía nav desktop: más legible y marcada; escala en pantallas muy grandes. */
const navLinkBase =
  "rounded-lg px-2.5 py-2 text-sm font-semibold tracking-wide text-zinc-200 transition-colors duration-200 md:text-[0.9375rem] xl:text-base xl:font-bold xl:tracking-wide 2xl:px-3 2xl:py-2.5 2xl:text-lg 2xl:tracking-tight hover:bg-white/[0.07] hover:text-cyan-300";

const navLinkMobile =
  "block w-full rounded-lg px-3 py-3 text-left text-base font-semibold tracking-wide text-zinc-200 transition-colors hover:bg-white/[0.07] hover:text-cyan-300";

/** Desktop: «Ejemplos» siempre con el aspecto de hover para que destaque. */
function primaryNavLinkClass(
  isActive: boolean,
  section: (typeof LANDING_LINKS)[number]["section"],
): string {
  if (section === "ejemplos") {
    return `${navLinkBase} md:bg-white/[0.07] md:text-cyan-300`;
  }
  if (isActive) return `${navLinkBase} text-cyan-200`;
  return navLinkBase;
}

function primaryNavLinkClassMobile(isActive: boolean): string {
  const line = "border-b-2 border-transparent transition-[border-color] duration-200";
  if (isActive) return `${navLinkMobile} text-cyan-200 ${line} border-cyan-400`;
  return `${navLinkMobile} ${line}`;
}

const LANDING_LINKS = [
  { href: "/#como-funciona", label: "¿Cómo funciona?", section: "como-funciona" as const },
  { href: "/#precios", label: "Precios", section: "precios" as const },
  { href: "/#preguntas-frecuentes", label: "Preguntas frecuentes", section: "preguntas-frecuentes" as const },
  { href: "/#soporte", label: "Soporte", section: "soporte" as const },
  { href: "/examples", label: "Ejemplos", section: "ejemplos" as const },
] as const;

function landingSectionId(href: (typeof LANDING_LINKS)[number]["href"]): string | null {
  if (!href.startsWith("/#")) return null;
  return href.slice(2) || null;
}

function scrollToLandingSection(id: string) {
  const el = document.getElementById(id);
  if (!el) return;
  const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  el.scrollIntoView({ behavior: reduce ? "auto" : "smooth", block: "start" });
}

const MD_MIN_WIDTH_QUERY = "(min-width: 768px)";

function subscribeMdMinWidth(onChange: () => void) {
  if (typeof window === "undefined") return () => {};
  const mq = window.matchMedia(MD_MIN_WIDTH_QUERY);
  mq.addEventListener("change", onChange);
  return () => mq.removeEventListener("change", onChange);
}

function getMdMinWidthSnapshot() {
  return typeof window !== "undefined" && window.matchMedia(MD_MIN_WIDTH_QUERY).matches;
}

/** Desktop: pill alineada al borde superior del Hero (main md:pt-20 + Hero mt-3). */
function collapsedBarOuterClass(pathname: string): string {
  if (pathname === "/") {
    return "md:top-[3.75rem] md:-translate-y-1/2";
  }
  if (pathname === "/examples") {
    return "md:top-[calc(max(7.75rem,env(safe-area-inset-top,0px)+6.75rem)+2rem+0.75rem)] md:-translate-y-1/2";
  }
  return "md:top-4 md:translate-y-0";
}

export const Navbar = () => {
  const pathname = usePathname();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isPinned, setIsPinned] = useState(false);
  const [activeSection, setActiveSection] = useState<LandingNavSectionId>("inicio");
  const [underline, setUnderline] = useState({ x: 0, width: 0 });

  const isMdViewport = useSyncExternalStore(subscribeMdMinWidth, getMdMinWidthSnapshot, () => false);

  const isExamplesRoute = pathname.startsWith("/examples");

  const showExpandedNavLinks =
    isPinned || pathname.startsWith("/legal") || (isExamplesRoute && isMdViewport);

  const navLinksTrackRef = useRef<HTMLDivElement>(null);
  const linkRefs = useRef<Partial<Record<LandingNavSectionId, HTMLAnchorElement | null>>>({});

  const syncActiveFromRoute = useCallback(() => {
    if (pathname.startsWith("/examples")) {
      setActiveSection("ejemplos");
      return;
    }
    if (pathname === "/") {
      setActiveSection(computeActiveHomeSection());
      return;
    }
    setActiveSection("inicio");
  }, [pathname]);

  useEffect(() => {
    if (isMdViewport) setMobileMenuOpen(false);
  }, [isMdViewport]);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [pathname]);

  useEffect(() => {
    syncActiveFromRoute();
  }, [syncActiveFromRoute]);

  useEffect(() => {
    if (!mobileMenuOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMobileMenuOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [mobileMenuOpen]);

  useEffect(() => {
    const onScroll = () => {
      setIsPinned((prev) => {
        if (prev) return window.scrollY > 40;
        return window.scrollY > 80;
      });
      if (pathname === "/") {
        const next = computeActiveHomeSection();
        startTransition(() => {
          setActiveSection(next);
        });
      }
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();

    return () => window.removeEventListener("scroll", onScroll);
  }, [pathname]);

  const updateUnderline = useCallback(() => {
    if (!isMdViewport || !showExpandedNavLinks) {
      setUnderline({ x: 0, width: 0 });
      return;
    }

    const sectionForLine: LandingNavSectionId | null =
      activeSection === "inicio" ? null : activeSection;

    if (!sectionForLine) {
      setUnderline({ x: 0, width: 0 });
      return;
    }

    const track = navLinksTrackRef.current;
    const linkEl = linkRefs.current[sectionForLine];
    if (!track || !linkEl) {
      setUnderline({ x: 0, width: 0 });
      return;
    }

    const tr = track.getBoundingClientRect();
    const lr = linkEl.getBoundingClientRect();
    setUnderline({ x: lr.left - tr.left, width: lr.width });
  }, [activeSection, showExpandedNavLinks, isMdViewport]);

  useLayoutEffect(() => {
    updateUnderline();
  }, [updateUnderline, isPinned, pathname]);

  useEffect(() => {
    if (!isMdViewport || !showExpandedNavLinks) return;

    const ro = new ResizeObserver(() => updateUnderline());
    const track = navLinksTrackRef.current;
    if (track) ro.observe(track);
    window.addEventListener("resize", updateUnderline);

    return () => {
      ro.disconnect();
      window.removeEventListener("resize", updateUnderline);
    };
  }, [isMdViewport, showExpandedNavLinks, updateUnderline]);

  function handleLandingNavClick(
    e: React.MouseEvent<HTMLAnchorElement>,
    href: (typeof LANDING_LINKS)[number]["href"],
    section: (typeof LANDING_LINKS)[number]["section"],
  ) {
    trackEvent({ name: "click_nav_link", params: { link: section } });
    if (pathname === "/" && landingSectionId(href)) {
      e.preventDefault();
      scrollToLandingSection(landingSectionId(href)!);
      setActiveSection(section);
    }
    if (pathname === "/" && href === "/examples") {
      setActiveSection("ejemplos");
    }
    setMobileMenuOpen(false);
  }

  function handleLogoClick(e: React.MouseEvent<HTMLAnchorElement>) {
    if (pathname === "/") {
      e.preventDefault();
      scrollToLandingSection("inicio");
      setActiveSection("inicio");
    }
    setMobileMenuOpen(false);
  }

  const logoBlock = (
    <Link
      href="/#inicio"
      className="relative flex shrink-0 items-center gap-1.5 transition-transform duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] lg:gap-2 2xl:gap-2.5"
      onClick={handleLogoClick}
    >
      <Image
        src="/selfia-logo-2.png"
        alt="Logo Self IA"
        width={80}
        height={80}
        className="w-12 transition-[width] duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] lg:w-20 2xl:w-[5.25rem]"
      />
      <p className="text-base font-bold tracking-tight text-white transition-[font-size] duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] lg:text-2xl 2xl:text-4xl">
        Selfia
      </p>
    </Link>
  );

  const renderDesktopLinks = () => (
    <div ref={navLinksTrackRef} className="relative flex flex-wrap items-center justify-end gap-x-1 gap-y-1 md:gap-x-0 lg:gap-3 2xl:gap-4">
      {LANDING_LINKS.map((item) => {
        const isActive = activeSection === item.section;
        return (
          <Link
            key={item.href}
            ref={(el) => {
              linkRefs.current[item.section] = el;
            }}
            href={item.href}
            className={primaryNavLinkClass(isActive, item.section)}
            aria-current={isActive ? "page" : undefined}
            onClick={(e) => handleLandingNavClick(e, item.href, item.section)}
          >
            {item.label}
          </Link>
        );
      })}
      <span
        aria-hidden
        className="pointer-events-none absolute bottom-0 left-0 h-[3px] rounded-full bg-cyan-400 motion-reduce:transition-none"
        style={{
          width: underline.width,
          transform: `translateX(${underline.x}px)`,
          opacity: underline.width > 0 ? 1 : 0,
          transition: "transform 280ms cubic-bezier(0.22, 1, 0.36, 1), width 280ms cubic-bezier(0.22, 1, 0.36, 1), opacity 160ms ease",
        }}
      />
    </div>
  );

  const collapsedShellClass = `top-0 opacity-0 transition-[top,opacity,transform] duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] md:opacity-100 ${collapsedBarOuterClass(pathname)}`;

  return (
    <>
      {/* Solo móvil (md:hidden): logo + menú hamburguesa */}
      <div className="fixed inset-x-0 top-0 z-110 w-full pt-[max(0px,env(safe-area-inset-top,0px))] md:hidden">
        <nav
          className="border-b border-white/10 bg-black/55 shadow-[0_12px_40px_-12px_rgba(0,0,0,0.72)] backdrop-blur-[14px]"
          aria-label="Principal"
        >
          <div className={`${SITE_CONTENT_OUTER} flex items-center justify-between gap-3 py-2.5`}>
            {logoBlock}
            <button
              type="button"
              className="rounded-lg p-2.5 text-zinc-100 transition-colors hover:bg-white/[0.08] hover:text-white"
              aria-expanded={mobileMenuOpen}
              aria-controls="mobile-nav-menu"
              aria-label={mobileMenuOpen ? "Cerrar menú" : "Abrir menú"}
              onClick={() => setMobileMenuOpen((o) => !o)}
            >
              {mobileMenuOpen ? (
                <X className="h-6 w-6" strokeWidth={2} aria-hidden />
              ) : (
                <Menu className="h-6 w-6" strokeWidth={2} aria-hidden />
              )}
            </button>
          </div>
          {mobileMenuOpen ? (
            <div
              id="mobile-nav-menu"
              className="border-t border-white/10 bg-black/60 px-2 pb-4 pt-1"
            >
              <div className={`${SITE_CONTENT_OUTER} flex flex-col gap-0.5`}>
                {LANDING_LINKS.map((item) => {
                  const isActive = activeSection === item.section;
                  return (
                    <Link
                      key={item.href}
                      href={item.href}
                      className={primaryNavLinkClassMobile(isActive)}
                      aria-current={isActive ? "page" : undefined}
                      onClick={(e) => handleLandingNavClick(e, item.href, item.section)}
                    >
                      {item.label}
                    </Link>
                  );
                })}
              </div>
            </div>
          ) : null}
        </nav>
      </div>

      {/* Desktop (md+): pill → expansión al scroll; ejemplos siempre expandido */}
      <div
        className={`fixed inset-x-0 z-110 hidden w-full motion-reduce:transition-none md:block ${
          showExpandedNavLinks
            ? "top-0 translate-y-0 opacity-100 transition-[top,opacity,transform] duration-700 ease-[cubic-bezier(0.16,1,0.3,1)]"
            : collapsedShellClass
        }`}
      >
        <div className="relative w-full">
          <nav
            className={`
        motion-reduce:transition-none
        ${
          showExpandedNavLinks
            ? "flex w-full justify-center border-b border-white/10 border-t-0 bg-black/50 py-2.5 shadow-[0_12px_40px_-12px_rgba(0,0,0,0.72)] backdrop-blur-[14px] md:rounded-b-3xl md:border-x md:border-white/10 md:py-3 2xl:py-3.5"
            : "mx-auto flex w-fit max-w-[calc(100vw-1rem)] items-center justify-center rounded-2xl border border-white/10 bg-black/[0.34] px-2.5 py-2 shadow-lg backdrop-blur-[12px]"
        }
        ${
          showExpandedNavLinks
            ? "transition-[width,max-width,border-radius,padding,background-color,box-shadow,border-color,backdrop-filter] duration-[850ms] ease-[cubic-bezier(0.16,1,0.3,1)]"
            : "transition-[width,max-width,border-radius,padding,background-color,box-shadow] duration-500 ease-[cubic-bezier(0.22,1,0.36,1)]"
        }
      `}
          >
            {showExpandedNavLinks ? (
              <div className={SITE_NAV_EXPANDED_ROW}>
                {logoBlock}
                {renderDesktopLinks()}
              </div>
            ) : (
              logoBlock
            )}
          </nav>
        </div>
      </div>
    </>
  );
};
